import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { getAllUsers } from '../firebase/helpers';
import { VIP_CONFIG } from './optimizationService';
import { USER_TASK_PROGRESS_COLLECTION } from './autoKnifeTriggerService';

const USER_TASK_PROGRESS_COLLECTION_NAME = USER_TASK_PROGRESS_COLLECTION;

/**
 * Get today's date key (YYYY-MM-DD format)
 */
const getTodayKey = () => {
  const now = new Date();
  return now.toISOString().split('T')[0]; // Returns YYYY-MM-DD
};

/**
 * Check if it's midnight (12 AM) - within the first minute of the day
 */
const isMidnight = () => {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  return hours === 0 && minutes < 1; // Check within first minute of midnight
};

/**
 * Reset a single user's progress to Set 1, Task 1
 */
export const resetUserProgress = async (userId, vipLevel = 'VIP1') => {
  try {
    const todayKey = getTodayKey();
    const config = VIP_CONFIG[vipLevel] || VIP_CONFIG['VIP1'];
    
    console.log(`🔄 Resetting progress for user ${userId} (${vipLevel}) to Set 1, Task 1`);
    
    // 1. Reset optimizationStatus - Set to Optimization 1, Task 0
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    if (statusDoc.exists()) {
      const statusData = statusDoc.data();
      const optimizations = {};
      
      // Reset all optimizations to unlocked, starting at task 0
      for (let i = 1; i <= config.optimizations; i++) {
        optimizations[i] = {
          status: i === 1 ? 'unlocked' : 'locked',
          locked: i !== 1,
          progress: '0/' + config.tasks
        };
      }
      
      await updateDoc(statusRef, {
        currentOptimization: 1,
        currentTask: 0,
        tasksCompleted: 0,
        todaysEarnings: 0,
        completedSets: 0,
        isLocked: false,
        pendingUnlock: false,
        lastCompletedDate: null,
        optimizations: optimizations,
        today: todayKey,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new status document
      const optimizations = {};
      for (let i = 1; i <= config.optimizations; i++) {
        optimizations[i] = {
          status: i === 1 ? 'unlocked' : 'locked',
          locked: i !== 1,
          progress: '0/' + config.tasks
        };
      }
      
      await setDoc(statusRef, {
        userId: userId,
        vipLevel: vipLevel,
        currentOptimization: 1,
        currentTask: 0,
        tasksCompleted: 0,
        todaysEarnings: 0,
        completedSets: 0,
        isLocked: false,
        pendingUnlock: false,
        optimizations: optimizations,
        today: todayKey,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    // 2. Reset optimizationProgress - Set to Set 1, Task 0
    const progressRef = doc(db, 'optimizationProgress', userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      await updateDoc(progressRef, {
        currentSet: 1,
        currentTask: 0,
        lastSavedDate: todayKey,
        lastSavedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(progressRef, {
        userId: userId,
        vipLevel: vipLevel,
        currentSet: 1,
        currentTask: 0,
        lastSavedDate: todayKey,
        lastSavedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    // 3. Reset userTaskProgress - Clear all task progress
    const taskProgressRef = doc(db, USER_TASK_PROGRESS_COLLECTION_NAME, userId);
    const taskProgressDoc = await getDoc(taskProgressRef);
    
    if (taskProgressDoc.exists()) {
      const taskProgress = taskProgressDoc.data();
      const updates = {
        lastResetDate: todayKey,
        lastResetAt: Timestamp.now(),
        knifeAppliedToday: false,
        updatedAt: Timestamp.now()
      };
      
      // Reset all tasks to 0 optimizations, but preserve historical data
      const totalTasks = config.totalTasks || (config.sets * config.tasksPerSet);
      for (let taskNum = 1; taskNum <= totalTasks; taskNum++) {
        const taskKey = `task${taskNum}`;
        const existingTaskData = taskProgress[taskKey] || {};
        
        updates[taskKey] = {
          ...existingTaskData,
          optimizations: 0,
          knifeApplied: false,
          knifeAppliedToday: false,
          dailyResetAt: Timestamp.now()
        };
      }
      
      await updateDoc(taskProgressRef, updates);
    }
    
    // 4. Create notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'info',
        title: '🌅 Daily Reset Complete',
        message: `Your progress has been reset to Set 1, Task 1. Start fresh and continue your optimization journey!`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating reset notification:', notifError);
    }
    
    console.log(`✅ Successfully reset user ${userId} to Set 1, Task 1`);
    
    return {
      success: true,
      userId,
      vipLevel,
      resetTo: { set: 1, task: 1 },
      todayKey
    };
  } catch (error) {
    console.error(`Error resetting user ${userId}:`, error);
    throw error;
  }
};

/**
 * Reset all users' progress to Set 1, Task 1
 * This should be called at midnight daily
 */
export const resetAllUsersProgress = async () => {
  try {
    const todayKey = getTodayKey();
    console.log(`🌅 Starting daily reset for all users - ${todayKey}`);
    
    // Get all users
    const users = await getAllUsers();
    const regularUsers = users.filter(user => 
      !user.role && 
      !user.admin && 
      !user.isDeleted
    );
    
    console.log(`📊 Found ${regularUsers.length} users to reset`);
    
    const results = {
      total: regularUsers.length,
      successful: 0,
      failed: 0,
      errors: []
    };
    
    // Reset each user
    for (const user of regularUsers) {
      try {
        const vipLevel = user.vipLevel || 'VIP1';
        await resetUserProgress(user.id, vipLevel);
        results.successful++;
      } catch (error) {
        console.error(`Failed to reset user ${user.id}:`, error);
        results.failed++;
        results.errors.push({
          userId: user.id,
          email: user.email,
          error: error.message
        });
      }
    }
    
    console.log(`✅ Daily reset completed: ${results.successful} successful, ${results.failed} failed`);
    
    // Store reset log
    try {
      const resetLogRef = doc(collection(db, 'dailyResetLogs'));
      await setDoc(resetLogRef, {
        date: todayKey,
        timestamp: Timestamp.now(),
        totalUsers: results.total,
        successful: results.successful,
        failed: results.failed,
        errors: results.errors,
        createdAt: serverTimestamp()
      });
    } catch (logError) {
      console.error('Error logging daily reset:', logError);
    }
    
    return results;
  } catch (error) {
    console.error('Error resetting all users:', error);
    throw error;
  }
};

/**
 * Check if daily reset is needed and perform it
 * This checks if the last reset was today, and if not, resets the user
 */
export const checkAndResetUserProgress = async (userId) => {
  try {
    const todayKey = getTodayKey();
    
      // Check optimizationStatus for last reset date
    const statusRef = doc(db, 'optimizationStatus', userId);
    const statusDoc = await getDoc(statusRef);
    
    // Get user to determine VIP level
    const users = await getAllUsers().catch(() => []);
    const user = users.find(u => u.id === userId);
    
    if (!user) {
      return { resetNeeded: false, error: 'User not found' };
    }
    
    const vipLevel = user.vipLevel || 'VIP1';
    
    if (statusDoc.exists()) {
      const statusData = statusDoc.data();
      const lastResetDate = statusData.today || statusData.lastResetDate;
      
      // If last reset was not today, reset is needed
      if (lastResetDate !== todayKey) {
        console.log(`🔄 Daily reset needed for user ${userId} - last reset: ${lastResetDate}, today: ${todayKey}`);
        return await resetUserProgress(userId, vipLevel);
      }
    } else {
      // No status document exists, initialize it
      return await resetUserProgress(userId, vipLevel);
    }
    
    return { resetNeeded: false, alreadyReset: true };
  } catch (error) {
    console.error('Error checking daily reset:', error);
    return { resetNeeded: false, error: error.message };
  }
};

/**
 * Initialize daily reset check
 * This should be called when the app starts or when a user logs in
 */
export const initializeDailyResetCheck = async (userId) => {
  try {
    // Just check if reset is needed (based on date)
    await checkAndResetUserProgress(userId);
  } catch (error) {
    console.error('Error initializing daily reset check:', error);
  }
};

/**
 * Start automatic daily reset scheduler
 * This checks every minute if it's midnight and resets all users
 * Should be called once when the app initializes
 */
export const startAutomaticDailyReset = () => {
  let lastResetDate = getTodayKey();
  let isResetting = false; // Flag to prevent multiple simultaneous resets
  
  // Check every minute if we've crossed midnight
  const checkInterval = setInterval(async () => {
    try {
      // Skip if already resetting
      if (isResetting) {
        return;
      }
      
      const todayKey = getTodayKey();
      
      // If date changed, it means we've crossed midnight
      if (todayKey !== lastResetDate) {
        console.log(`🌅 Midnight detected! Date changed from ${lastResetDate} to ${todayKey}`);
        console.log('🔄 Starting automatic daily reset for all users...');
        
        isResetting = true;
        lastResetDate = todayKey;
        
        try {
          // Reset all users
          const result = await resetAllUsersProgress();
          console.log(`✅ Automatic daily reset completed: ${result.successful} successful, ${result.failed} failed`);
        } catch (resetError) {
          console.error('Error during automatic daily reset:', resetError);
        } finally {
          isResetting = false;
        }
      }
    } catch (error) {
      console.error('Error in automatic daily reset check:', error);
      isResetting = false;
    }
  }, 60000); // Check every minute
  
  console.log('✅ Automatic daily reset scheduler started - will reset all users at midnight');
  
  // Return cleanup function
  return () => {
    clearInterval(checkInterval);
    console.log('🛑 Automatic daily reset scheduler stopped');
  };
};

